import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, HostListener, inject, Input, Output } from '@angular/core';
import { ChatComposerSubmit } from '../../models/chat.models';
import { RemmiConfirmService } from '@common/remmi-confirm/remmi-confirm.service';
import { ChatStore } from '../../services/chat.store';
import { ChatAvatarComponent } from '../chat-avatar/chat-avatar.component';
import { ChatComposerComponent } from '../chat-composer/chat-composer.component';
import { ChatMessageListComponent } from '../chat-message-list/chat-message-list.component';

/**
 * The middle column: conversation header, transcript, composer.
 *
 * Used by the full page and the floating widget. `compact` shrinks the header for the popover
 * and swaps the "Group info" button for a back arrow, since the popover has no room for a third
 * column.
 */
@Component({
  selector: 'app-chat-conversation',
  standalone: true,
  imports: [CommonModule, ChatAvatarComponent, ChatComposerComponent, ChatMessageListComponent],
  templateUrl: './chat-conversation.component.html',
  styleUrls: ['./chat-conversation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatConversationComponent {
  readonly store = inject(ChatStore);
  private readonly remmiConfirm = inject(RemmiConfirmService);
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);

  /** Popover variant: tighter header, back arrow instead of the info panel toggle. */
  @Input() compact = false;

  /** Whether the Group Info panel is currently open (page only). */
  @Input() infoOpen = false;

  @Output() toggleInfo = new EventEmitter<void>();
  @Output() back = new EventEmitter<void>();
  @Output() manageGroup = new EventEmitter<void>();

  menuOpen = false;

  get isGroup(): boolean {
    return this.store.activeDetail()?.type === 'group';
  }

  send(submit: ChatComposerSubmit): void {
    this.store.send(submit);
  }

  onTyping(): void {
    this.store.notifyTyping();
  }

  startCall(): void {
    this.store.startVideoCall();
  }

  /** Reading resumes the moment the thread is clicked back into focus. */
  @HostListener('click')
  onFocusThread(): void {
    if (this.store.activeId()) {
      this.store.markReadNow();
    }
  }

  toggleMenu(): void {
    this.menuOpen = !this.menuOpen;
  }

  copyGroup(): void {
    const detail = this.store.activeDetail();
    this.menuOpen = false;
    if (detail) {
      this.store.copyGroup(detail.conversation_id);
    }
  }

  leaveGroup(): void {
    const detail = this.store.activeDetail();
    this.menuOpen = false;
    if (!detail) {
      return;
    }
    this.remmiConfirm
      .ask({
        title: 'Leave Group?',
        message: "You'll stop receiving its messages.",
        subject: detail.name,
        confirmLabel: 'Leave',
        tone: 'warning',
        icon: 'pi pi-sign-out',
      })
      .subscribe(isConfirmed => {
        if (isConfirmed) {
          this.store.leaveGroup(detail.conversation_id);
        }
      });
  }

  deleteGroup(): void {
    const detail = this.store.activeDetail();
    this.menuOpen = false;
    if (!detail) {
      return;
    }
    this.remmiConfirm
      .delete({
        title: 'Delete Group?',
        message: 'This removes the group for everyone.',
        subject: detail.name,
      })
      .subscribe(isConfirmed => {
        if (isConfirmed) {
          this.store.deleteGroup(detail.conversation_id);
        }
      });
  }

  openManage(): void {
    this.menuOpen = false;
    this.manageGroup.emit();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.menuOpen && !this.host.nativeElement.contains(event.target as Node)) {
      this.menuOpen = false;
    }
  }
}
